import React, { useState, useEffect, useMemo } from 'react';
import { clientUpcomingAuditsService } from '../services';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Calendar, 
  Building2, 
  AlertCircle, 
  Loader2,
  Clock,
  MapPin,
  ChevronRight,
  Filter,
  Search
} from 'lucide-react';

/**
 * Client Upcoming Audits Page - Phase 2.3B
 * Allows client users to view upcoming scheduled audits for their accessible sites.
 * Read-only view - no editing, confirmation, or rescheduling.
 */
const ClientUpcomingAuditsPage = () => {
  const { anonymise } = useImpersonation();
  // State
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(60); // Default 60 days
  const [siteSearch, setSiteSearch] = useState(''); // Site search filter

  // Date range options
  const dateRangeOptions = [
    { value: 7, label: 'Next 7 days' },
    { value: 14, label: 'Next 14 days' },
    { value: 30, label: 'Next 30 days' },
    { value: 60, label: 'Next 60 days' },
  ];

  // Calculate date range
  const formatLocalDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const getDateRange = () => {
    const today = new Date();
    const toDate = new Date();
    toDate.setDate(today.getDate() + dateRange);
    
    return {
      from: formatLocalDate(today),
      to: formatLocalDate(toDate)
    };
  };

  // Load audits
  useEffect(() => {
    const fetchAudits = async () => {
      setLoading(true);
      setError(null);
      try {
        const { from, to } = getDateRange();
        const data = await clientUpcomingAuditsService.listUpcomingAudits({ 
          from, 
          to, 
          limit: 200 
        });
        setAudits(data);
      } catch (err) {
        if (err.response?.status === 403 || err.response?.status === 401) {
          setError("You don't have access to view upcoming audits.");
        } else {
          setError("Something went wrong. Please try again.");
        }
        setAudits([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAudits();
  }, [dateRange]);

  // Group audits by date for display (with site filter applied)
  const filteredAudits = useMemo(() => {
    if (!siteSearch) return audits;
    const search = siteSearch.toLowerCase();
    return audits.filter(audit => 
      audit.site_name?.toLowerCase().includes(search) ||
      audit.group_name?.toLowerCase().includes(search)
    );
  }, [audits, siteSearch]);

  const groupedAudits = useMemo(() => {
    const groups = {};
    filteredAudits.forEach(audit => {
      const dateKey = audit.scheduled_date;
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(audit);
    });
    return groups;
  }, [filteredAudits]);

  // Sort dates
  const sortedDates = useMemo(() => {
    return Object.keys(groupedAudits).sort((a, b) => new Date(a) - new Date(b));
  }, [groupedAudits]);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Format short date
  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short'
    });
  };

  // Check if date is today
  const isToday = (dateStr) => {
    const today = formatLocalDate(new Date());
    return dateStr === today;
  };

  // Check if date is this week
  const isThisWeek = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const weekFromNow = new Date();
    weekFromNow.setDate(today.getDate() + 7);
    return date >= today && date <= weekFromNow;
  };

  // No sites assigned state
  if (!loading && audits.length === 0 && !error) {
    // Could be no sites or no upcoming audits - check access summary
    return (
      <div className="space-y-6" data-testid="client-upcoming-audits-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Upcoming Audits</h1>
          <p className="text-sm text-slate-500 mt-1">
            View scheduled audits for your sites
          </p>
        </div>

        {/* Date Range Filter */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Filter className="h-4 w-4" />
                <span>Show:</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {dateRangeOptions.map(option => (
                  <Button
                    key={option.value}
                    variant={dateRange === option.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDateRange(option.value)}
                    className={dateRange === option.value ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-3 py-8">
              <Calendar className="h-12 w-12 text-slate-300" />
              <h3 className="font-medium text-slate-700">No Upcoming Audits</h3>
              <p className="text-sm text-slate-500 text-center max-w-md">
                No upcoming audits scheduled in the selected period. 
                Try selecting a longer date range or contact your account manager.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-upcoming-audits-page">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Upcoming Audits</h1>
        <p className="text-sm text-slate-500 mt-1">
          View scheduled audits for your sites
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Filter className="h-4 w-4" />
              <span>Show:</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {dateRangeOptions.map(option => (
                <Button
                  key={option.value}
                  variant={dateRange === option.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDateRange(option.value)}
                  data-testid={`range-${option.value}`}
                  className={dateRange === option.value ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                >
                  {option.label}
                </Button>
              ))}
            </div>
            {/* Site Search Filter */}
            <div className="relative ml-auto">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Filter by site..."
                value={siteSearch}
                onChange={(e) => setSiteSearch(e.target.value)}
                className="pl-9 h-9 w-[200px]"
                data-testid="site-search-filter"
              />
            </div>
            {!loading && (
              <span className="text-sm text-slate-500">
                {filteredAudits.length} audit{filteredAudits.length !== 1 ? 's' : ''} 
                {siteSearch && ` (filtered from ${audits.length})`}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <span className="text-red-700">{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          <span className="ml-2 text-slate-600">Loading audits...</span>
        </div>
      )}

      {/* Audits List Grouped by Date */}
      {!loading && !error && sortedDates.length > 0 && (
        <div className="space-y-4">
          {sortedDates.map(dateKey => (
            <Card key={dateKey} className={isToday(dateKey) ? 'border-emerald-300 bg-emerald-50/30' : ''}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className={`h-4 w-4 ${isToday(dateKey) ? 'text-emerald-600' : 'text-slate-500'}`} />
                  <span className={isToday(dateKey) ? 'text-emerald-700' : 'text-slate-700'}>
                    {formatDate(dateKey)}
                  </span>
                  {isToday(dateKey) && (
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                      Today
                    </span>
                  )}
                  {!isToday(dateKey) && isThisWeek(dateKey) && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                      This Week
                    </span>
                  )}
                  <span className="text-xs text-slate-400 font-normal ml-auto">
                    {groupedAudits[dateKey].length} audit{groupedAudits[dateKey].length !== 1 ? 's' : ''}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {groupedAudits[dateKey].map(audit => (
                    <div
                      key={audit.job_id}
                      data-testid={`audit-item-${audit.job_id}`}
                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-emerald-600" />
                          <span className="font-medium text-slate-900">{anonymise(audit.site_name, 'site')}</span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                          {audit.group_name && (
                            <span className="flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {anonymise(audit.group_name, 'group')}
                            </span>
                          )}
                          {audit.service_name && (
                            <span className="flex items-center gap-1">
                              <ChevronRight className="h-3 w-3" />
                              {audit.service_name}
                            </span>
                          )}
                          {audit.end_date && audit.end_date !== audit.scheduled_date && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Until {formatShortDate(audit.end_date)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Scheduled
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="bg-blue-100 rounded-full p-2">
              <AlertCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-blue-900 mb-1">
                About Upcoming Audits
              </h3>
              <p className="text-sm text-blue-700">
                This page shows scheduled audits for sites assigned to your account. 
                For any questions about scheduling or to request changes, please contact your account manager.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientUpcomingAuditsPage;
